import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useState } from "react";
import { ForgotPasswordForm } from "@/components/auth/forgot-password-form";
import { VerifyEmailMessage } from "@/components/auth/verify-email-message";
import { ForgotPasswordPayload } from "@/schemas/auth";
import { useForgotPassword } from "@/queries/use-forgot-password";
import { z } from "zod";
import { AUTH_ROUTES } from "@/utils/navigation";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/(auth)/forgot-password/")({
  validateSearch: z.object({
    step: z.number().optional().default(0),
  }),
  component: ForgotPasswordComponent,
});

function ForgotPasswordComponent() {
  const { step = 0 } = useSearch({ from: "/(auth)/forgot-password/" });
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { mutate: forgotPassword, isPending: isSubmittingForgotPassword } =
    useForgotPassword({
      onSuccess: () => {
        navigate({
          to: AUTH_ROUTES.FORGOT_PASSWORD,
          search: { step: 1 },
        });
      },
      onError: (error: Error) => {
        console.error("Forgot password error:", error);
        setError(t("pages:forgot-password.error"));
      },
    });

  const handleSubmit = (payload: ForgotPasswordPayload) => {
    setError(null);
    forgotPassword(payload);
  };

  const handleAcknowledge = () => {
    navigate({ to: AUTH_ROUTES.SIGN_IN });
  };

  return (
    <div className="flex w-full flex-col items-center md:items-start">
      {step === 0 && (
        <>
          <ForgotPasswordForm
            onSubmit={handleSubmit}
            isSubmitting={isSubmittingForgotPassword}
            error={error}
          />
        </>
      )}
      {step === 1 && <VerifyEmailMessage onAcknowledge={handleAcknowledge} />}
    </div>
  );
}
